/**
 * Minimal cookie helpers replacing the `js-cookie` package. We only use reading
 * and removal, but a `set` is included since `remove` is defined in terms of it.
 * Value decoding mirrors js-cookie's default converter.
 */

interface CookieAttributes {
  /** Expiry in days (number) or an explicit Date. */
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: string;
}

function decode(value: string): string {
  return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
}

export function get(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) {
    return undefined;
  }

  for (const cookie of document.cookie.split('; ')) {
    const eq = cookie.indexOf('=');
    if (eq < 0) {
      continue;
    }
    if (decode(cookie.slice(0, eq)) === name) {
      let value = cookie.slice(eq + 1);
      if (value[0] === '"') {
        value = value.slice(1, -1);
      }
      return decode(value);
    }
  }

  return undefined;
}

export function set(name: string, value: string, attributes: CookieAttributes = {}): void {
  if (typeof document === 'undefined') {
    return;
  }

  const {expires, path = '/', domain, secure, sameSite} = attributes;

  let str = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires != null) {
    const date = typeof expires === 'number' ? new Date(Date.now() + expires * 864e5) : expires;
    str += `; expires=${date.toUTCString()}`;
  }
  if (path) {
    str += `; path=${path}`;
  }
  if (domain) {
    str += `; domain=${domain}`;
  }
  if (secure) {
    str += '; secure';
  }
  if (sameSite) {
    str += `; samesite=${sameSite}`;
  }

  document.cookie = str;
}

export function remove(name: string, attributes: CookieAttributes = {}): void {
  set(name, '', {...attributes, expires: -1});
}

export default {get, set, remove};
